import { useState } from 'react';

import { defaultCountryCode } from '@components/country-code/constants';
import { CountryData } from '@components/country-code/types';

import { MoveToCofeeAction, MoveToCofeeStep } from './constants';
import {
  AddToMemberListFormData,
  MoveToCofeeContact,
  QuickCollectFormData,
  QuickCollectGroupFormData,
  ReviewField
} from './types';
import useQuickCollectAmountLimits from './useQuickCollectAmountLimits';
import useSupportedPhoneCountries from './useSupportedPhoneCountries';
import {
  isMoveToCofeeMobileInvalid,
  isMoveToCofeeNameInvalid
} from './validation';

import { LeadType } from '../../types/common';
import { formatMobileNumber } from '../../utils/common';

type LeadContact = Pick<LeadType, 'name' | 'mobile_number' | 'country_code'>;

const getInitialContact = (lead: LeadContact): MoveToCofeeContact => ({
  name: lead.name,
  mobileNumber: lead.mobile_number || '',
  countryCode: (lead.country_code || defaultCountryCode).replace('+', '')
});

const getInitialMemberListForm = (
  lead: LeadContact
): AddToMemberListFormData => ({
  ...getInitialContact(lead),
  branchId: '',
  branchName: '',
  assignToGroup: false,
  groupId: '',
  groupName: ''
});

const getInitialQuickCollectForm = (
  lead: LeadContact
): QuickCollectFormData => ({
  ...getInitialContact(lead),
  branchId: '',
  branchName: '',
  amount: '',
  notes: ''
});

const getInitialQuickCollectGroupForm = (
  lead: LeadContact
): QuickCollectGroupFormData => ({
  ...getInitialContact(lead),
  branchId: '',
  branchName: '',
  amount: '',
  notes: '',
  groupId: '',
  groupName: ''
});

const isAmountValid = (
  amount: string,
  minAmount: number,
  maxAmount: number
) => {
  const num = Number(amount);
  return num >= minAmount && num <= maxAmount;
};

const isNotesValid = (notes: string) => notes.trim().length >= 3;

const isContactValid = (
  form: MoveToCofeeContact,
  supportedCountries: CountryData[]
) =>
  !isMoveToCofeeNameInvalid(form.name) &&
  !isMoveToCofeeMobileInvalid(
    form.mobileNumber,
    form.countryCode,
    supportedCountries
  );

const isMemberListValid = (
  form: AddToMemberListFormData,
  supportedCountries: CountryData[]
) => !!(isContactValid(form, supportedCountries) && form.branchId);

const isQuickCollectValid = (
  form: QuickCollectFormData,
  minAmount: number,
  maxAmount: number,
  supportedCountries: CountryData[]
) =>
  !!(
    isContactValid(form, supportedCountries) &&
    form.branchId &&
    form.amount &&
    isAmountValid(form.amount, minAmount, maxAmount) &&
    form.notes &&
    isNotesValid(form.notes)
  );

const isQuickCollectGroupValid = (
  form: QuickCollectGroupFormData,
  minAmount: number,
  maxAmount: number,
  supportedCountries: CountryData[]
) =>
  !!(
    isContactValid(form, supportedCountries) &&
    form.branchId &&
    form.amount &&
    isAmountValid(form.amount, minAmount, maxAmount) &&
    form.notes &&
    isNotesValid(form.notes) &&
    form.groupId
  );

const getMobileReviewField = (form: MoveToCofeeContact): ReviewField => ({
  label: 'Lead Mobile',
  value: formatMobileNumber(form.mobileNumber, form.countryCode)
});

const getMemberListReviewFields = (
  form: AddToMemberListFormData
): ReviewField[] => {
  const fields: ReviewField[] = [
    { label: 'Lead Name', value: form.name },
    getMobileReviewField(form),
    { label: 'Branch', value: form.branchName }
  ];
  if (form.assignToGroup && form.groupName) {
    fields.push({ label: 'Group', value: form.groupName });
  }
  return fields;
};

const getQuickCollectReviewFields = (
  form: QuickCollectFormData
): ReviewField[] => [
  { label: 'Lead Name', value: form.name },
  getMobileReviewField(form),
  { label: 'Branch', value: form.branchName },
  { label: 'Amount', value: form.amount },
  { label: 'Notes', value: form.notes }
];

const getQuickCollectGroupReviewFields = (
  form: QuickCollectGroupFormData
): ReviewField[] => [
  { label: 'Lead Name', value: form.name },
  getMobileReviewField(form),
  { label: 'Branch', value: form.branchName },
  { label: 'Group', value: form.groupName },
  { label: 'Amount', value: form.amount },
  { label: 'Notes', value: form.notes }
];

const useMoveToCofeeForm = (lead: LeadContact) => {
  const { minAmount, maxAmount } = useQuickCollectAmountLimits();
  const supportedCountries = useSupportedPhoneCountries();

  const [memberListForm, setMemberListForm] = useState<AddToMemberListFormData>(
    getInitialMemberListForm(lead)
  );
  const [quickCollectForm, setQuickCollectForm] =
    useState<QuickCollectFormData>(getInitialQuickCollectForm(lead));
  const [quickCollectGroupForm, setQuickCollectGroupForm] =
    useState<QuickCollectGroupFormData>(getInitialQuickCollectGroupForm(lead));

  const updateMemberListForm = (data: Partial<AddToMemberListFormData>) =>
    setMemberListForm((prev) => ({ ...prev, ...data }));

  const updateQuickCollectForm = (data: Partial<QuickCollectFormData>) =>
    setQuickCollectForm((prev) => ({ ...prev, ...data }));

  const updateQuickCollectGroupForm = (
    data: Partial<QuickCollectGroupFormData>
  ) => setQuickCollectGroupForm((prev) => ({ ...prev, ...data }));

  const isContinueDisabled = (
    currentStep: MoveToCofeeStep,
    selectedAction: MoveToCofeeAction | null
  ) => {
    if (currentStep === MoveToCofeeStep.CHOOSE_ACTION) {
      return !selectedAction;
    }
    if (currentStep === MoveToCofeeStep.CONFIGURE) {
      if (selectedAction === MoveToCofeeAction.ADD_TO_MEMBER_LIST) {
        return !isMemberListValid(memberListForm, supportedCountries);
      }
      if (selectedAction === MoveToCofeeAction.QUICK_COLLECT) {
        return !isQuickCollectValid(
          quickCollectForm,
          minAmount,
          maxAmount,
          supportedCountries
        );
      }
      if (selectedAction === MoveToCofeeAction.QUICK_COLLECT_ADD_TO_GROUP) {
        return !isQuickCollectGroupValid(
          quickCollectGroupForm,
          minAmount,
          maxAmount,
          supportedCountries
        );
      }
    }
    return false;
  };

  const getReviewFields = (
    selectedAction: MoveToCofeeAction | null
  ): ReviewField[] => {
    if (selectedAction === MoveToCofeeAction.ADD_TO_MEMBER_LIST) {
      return getMemberListReviewFields(memberListForm);
    }
    if (selectedAction === MoveToCofeeAction.QUICK_COLLECT) {
      return getQuickCollectReviewFields(quickCollectForm);
    }
    if (selectedAction === MoveToCofeeAction.QUICK_COLLECT_ADD_TO_GROUP) {
      return getQuickCollectGroupReviewFields(quickCollectGroupForm);
    }
    return [];
  };

  return {
    memberListForm,
    quickCollectForm,
    quickCollectGroupForm,
    updateMemberListForm,
    updateQuickCollectForm,
    updateQuickCollectGroupForm,
    isContinueDisabled,
    getReviewFields
  };
};

export default useMoveToCofeeForm;
